import "../../../candidate-intelligence/results/styles.css";
import type { InterviewPanelResponse } from "@shared/labs/level-assessment-interview-panel";
import { ChevronLeft, Copy, Eye, EyeOff, Printer } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useRoute } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { C, DM, UI } from "../../results/ui-tokens";
import { Muted } from "../../sets/ui";
import { CandidateKeyframes } from "../candidate-ui";
import { buildGuideText, derivePanelActs } from "./derive";
import {
  ClarifySection,
  EvidenceInventory,
  KnownReads,
  PanelHeader,
  PanelSkeleton,
  ResolvedSection,
} from "./sections";
import { ActHeader, SectionEyebrow } from "./ui";

/** Fast enough that a first open feels near-instant, calm enough not to hammer. */
const POLL_MS = 2500;

/** Blind-mode persistence — the interviewer's independent-measurement view. */
const LS_BLIND = "la:panel-blind";

function initialBlind(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("blind") === "1") return true;
    return localStorage.getItem(LS_BLIND) === "1";
  } catch {
    return false;
  }
}

/** Panel-scoped CSS introduced here (the app has none): the disclosure chevron
 *  rotation, the responsive stacking hook (`know-grid` → 1 column on narrow
 *  viewports), and the print rules (hide controls, drop the warm canvas, keep
 *  cards intact). The `beforeprint` handler opens every `<details>`. */
const PANEL_CSS = `
.ip-sheet details > summary { list-style: none; }
.ip-sheet details > summary::-webkit-details-marker { display: none; }
.ip-sheet .chev { transition: transform 150ms ease; }
.ip-sheet details[open] > summary .chev { transform: rotate(180deg); }
@media (max-width: 720px) {
  .ip-sheet .know-grid { grid-template-columns: 1fr !important; }
}
@media print {
  .no-print { display: none !important; }
  .ip-sheet, body { background: #fff !important; }
  .avoid-break { break-inside: avoid; }
}`;

export default function LevelAssessmentInterviewPanel() {
  const [, params] = useRoute("/labs/level-assessment/candidates/:candidateId/rounds/:roundId");
  const candidateId = params?.candidateId ?? "";
  const roundId = params?.roundId ?? "";
  const { toast } = useToast();

  const [data, setData] = useState<InterviewPanelResponse | null>(null);
  const [blind, setBlind] = useState<boolean>(initialBlind);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Monotonic request id — only the latest in-flight request may touch state, so
  // a slow / out-of-order poll response can't revert a fresher one (or setState
  // after the round changed / the view unmounted).
  const reqRef = useRef(0);

  const load = useCallback(
    async (opts?: { silent?: boolean }): Promise<InterviewPanelResponse | null> => {
      const myReq = ++reqRef.current;
      if (!opts?.silent) setLoading(true);
      try {
        const res = (await rpcClient.labs.levelAssessmentInterviewPanelGet({
          candidateId,
          roundId,
        })) as InterviewPanelResponse;
        if (myReq !== reqRef.current) return null; // superseded → drop
        setData(res);
        setError(null);
        return res;
      } catch (e) {
        if (myReq !== reqRef.current) return null;
        setError(e instanceof Error ? e.message : "Erro ao carregar o painel.");
        return null;
      } finally {
        if (myReq === reqRef.current && !opts?.silent) setLoading(false);
      }
    },
    [candidateId, roundId],
  );

  useEffect(() => {
    if (candidateId && roundId) void load();
    // Invalidate any in-flight request when the round changes / we unmount.
    return () => {
      reqRef.current++;
    };
  }, [candidateId, roundId, load]);

  // Poll while the panel is still generating; stop as soon as it's terminal.
  const isGenerating = data?.status === "generating";
  useEffect(() => {
    if (!isGenerating) return;
    const token = { cancelled: false };
    const id = setInterval(() => {
      void (async () => {
        const res = await load({ silent: true });
        if (token.cancelled || !res) return;
        if (res.status !== "generating") clearInterval(id);
      })();
    }, POLL_MS);
    return () => {
      token.cancelled = true;
      clearInterval(id);
    };
  }, [isGenerating, load]);

  // Open every collapsed <details> before printing so nothing is hidden in the
  // PDF, then restore exactly those afterwards (preserving any the user had
  // manually expanded).
  useEffect(() => {
    let opened: Element[] = [];
    const expand = () => {
      opened = Array.from(document.querySelectorAll("details")).filter(
        (d) => !(d as HTMLDetailsElement).open,
      );
      opened.forEach((d) => {
        d.setAttribute("open", "");
      });
    };
    const restore = () => {
      opened.forEach((d) => {
        d.removeAttribute("open");
      });
      opened = [];
    };
    window.addEventListener("beforeprint", expand);
    window.addEventListener("afterprint", restore);
    return () => {
      window.removeEventListener("beforeprint", expand);
      window.removeEventListener("afterprint", restore);
    };
  }, []);

  const panel = data?.status === "ready" ? data.panel : null;
  const serverError = data?.status === "error" ? data.error : null;

  // Split the flat contract into the page's 3-act structure (pure, memoized).
  const acts = useMemo(() => (panel ? derivePanelActs(panel) : null), [panel]);

  const toggleBlind = useCallback(() => {
    setBlind((b) => {
      try {
        localStorage.setItem(LS_BLIND, b ? "0" : "1");
      } catch {
        // persistence is best-effort
      }
      return !b;
    });
  }, []);

  const copyGuide = useCallback(async () => {
    if (!panel || !acts) return;
    const text = buildGuideText(panel, acts);
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Guia copiado",
        description: "Cole onde precisar: texto puro, pronto para a entrevista.",
      });
    } catch {
      toast({ title: "Copie o guia", description: text.slice(0, 240) });
    }
  }, [panel, acts, toast]);

  return (
    <div className="ci-stripe ci-stripe-page ip-sheet">
      {/** biome-ignore lint/security/noDangerouslySetInnerHtml: static panel CSS, no user data */}
      <style dangerouslySetInnerHTML={{ __html: PANEL_CSS }} />
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1120 }}>
        <CandidateKeyframes />

        {/* Top bar */}
        <div
          className="no-print"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Link
            href={`/labs/level-assessment/candidates/${candidateId}`}
            style={{
              ...UI,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 14,
              color: C.ink50,
              textDecoration: "none",
            }}
          >
            <ChevronLeft size={16} /> Voltar ao candidato
          </Link>
          <div style={{ display: "flex", gap: 8 }}>
            {panel && (
              <TopButton
                icon={blind ? Eye : EyeOff}
                label={blind ? "Mostrar estimativa" : "Modo cego"}
                onClick={toggleBlind}
              />
            )}
            <TopButton icon={Printer} label="Imprimir" onClick={() => window.print()} />
            {panel && !blind && <TopButton icon={Copy} label="Copiar guia" onClick={copyGuide} />}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <SectionEyebrow>Painel de entrevista</SectionEyebrow>
        </div>

        {(error || serverError) && (
          <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
            <Muted>Falha ao montar o painel: {error ?? serverError}</Muted>
            <button
              type="button"
              onClick={() => void load()}
              style={{
                ...UI,
                fontSize: 13,
                fontWeight: 600,
                color: C.red,
                background: "transparent",
                border: `1px solid ${C.red20}`,
                borderRadius: 999,
                padding: "5px 12px",
                cursor: "pointer",
              }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!error && !serverError && (loading || isGenerating) && <PanelSkeleton />}

        {panel && acts && (
          <>
            <PanelHeader panel={panel} blind={blind} />

            {!blind && (
              <>
                <ActHeader n={1} title="Avaliação atual" />
                <EvidenceInventory panel={panel} />
                <KnownReads panel={panel} />
              </>
            )}

            <ActHeader n={blind ? 1 : 2} title="Pontos cegos" />
            <ClarifySection cards={acts.open} blind={blind} />

            {acts.resolved.length > 0 && (
              <>
                <ActHeader n={blind ? 2 : 3} title="Já esclarecido" />
                <ResolvedSection items={acts.resolved} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TopButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Printer;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...DM,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        height: 38,
        padding: "0 15px",
        fontSize: 14,
        fontWeight: 700,
        color: C.ink,
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 999,
        cursor: "pointer",
      }}
    >
      <Icon size={15} /> {label}
    </button>
  );
}
